import React, { useEffect, useState }     from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useController } from '@rest-hooks/react';

import OrganizationResource from 'resources/organization/OrganizationResource';
import PublicThemeResource from 'resources/public/ThemeResource';
import OrganizationElementResource from 'resources/organization/PageElementResource';
import RewardResource from "resources/organization/RewardResource";

import Layout             from 'components/layouts/index';
import CrowdclassFallback from 'components/CrowdclassFallback';

import OrganizationEditor from './OrganizationEditor';
import Preview from './Preview';
import PreviewModal from './PreviewModal';
import LinksEditor from './LinksEditor';
import { phoneFrameStyles } from './phoneFrameStyles';

import { useError } from 'utils/useErrorController';
import { activePasses } from 'utils/filterFunctions';

import { Grid, Box } from '@mui/material';

const pageInfo = {
  name: "Club Profile & Link-In-Bio",
  description: "Customize your Club Profile elements and Link In-Bio page and share it with your audience",
};

const OrganizationSettingsV2 = () => {

  const { fetch } = useController();
  const { handleError } = useError();
  
  const params = useParams();
  const organizationId = parseInt(params.organizationId);

  const [organization, setOrganization] = useState({});
  const [originalOrganization, setOriginalOrganization] = useState({});
  const [socials, setSocials] = useState([]);
  const [links, setLinks] = useState([]);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [passes, setPasses] = useState([]);

  const fetchOrganizationWithTheme = async () => {
    try {
      const themesArray = await fetch(PublicThemeResource.list(), { id: organizationId });

      const { highlight_color, custom_cta, custom_cta_action, primary_text_color, secondary_text_color, primary_background_color, border_color } = themesArray[0]?.published_theme?.theme || {};

      const theme = primary_text_color === 'white' ? 'dark' : 'light';

      const organizationData = await fetch(OrganizationResource.detail(), { id: organizationId });

      const newOrganization = { 
        ...organizationData, 
        highlight_color, 
        custom_cta, 
        custom_cta_action, 
        theme,
        primary_text_color,
        secondary_text_color,
        primary_background_color,
        border_color
      };

      setOrganization(newOrganization);
      setOriginalOrganization(newOrganization);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchOrganizationElements = async () => {
    try {
      const { socials, links } = await fetch(OrganizationElementResource.detail(), { organization_id: organizationId, id: 1 });
      setSocials(socials);
      setLinks(links);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchPasses = async () => {
    try {
      const rewards = await fetch(RewardResource.list(), { organization_id: organizationId, });
      const passes = rewards.filter(activePasses);
      setPasses(passes);
    } catch (error) {
      handleError(error);
    }
  };

  const initialize = async () => {
    await Promise.all([
      fetchOrganizationWithTheme(),
      fetchOrganizationElements(),
      fetchPasses()
    ]);
  };

  const handleOrganizationDetailsAndThemeSave = () => {
    fetchOrganizationWithTheme();
  };

  const handleLinksEditorSave = () => {
    fetchOrganizationElements();
  };

  const handleSocialsEditorSave = () => {
    fetchOrganizationElements();
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Layout context='teacher'
            activeMenuItem='profile'
            pageInfo={pageInfo}
            v2
            >
      
      {Object.keys(organization).length === 0 ? (
        <CrowdclassFallback />
      ) : (
        <Grid container spacing={3} sx={{ minHeight: "calc(100vh - 250px)" }}>
          <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2 }}>

            <OrganizationEditor organization={organization}
                                onOrganizationChange={setOrganization}
                                onPreviewModalOpen={() => {setOpenPreviewModal(true)}}
                                originalOrganization={originalOrganization}
                                socials={socials}
                                onOrganizationDetailsAndThemeSave={handleOrganizationDetailsAndThemeSave}
                                onSocialsEditorSave={handleSocialsEditorSave} />

            <LinksEditor links={links} passes={passes} onSave={handleLinksEditorSave} />

          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{
              display: 'flex', //default in preview modal
              flexDirection: 'column', //default in preview modal
              top: '24px', //needed for sticky positioning
              position: 'sticky', //needed for sticky positioning
              ...phoneFrameStyles
            }} >
              <Preview originalOrganizationSlug={originalOrganization.slug} organization={organization} socials={socials} links={links} />
            </Box>
          </Grid>
        </Grid>
      )}

      <PreviewModal open={openPreviewModal} onClose={() => setOpenPreviewModal(false)} originalOrganizationSlug={originalOrganization.slug} organization={organization} socials={socials} links={links}/>
    </Layout>
  );
};

OrganizationSettingsV2.propTypes = {
  match: PropTypes.object
};

export default OrganizationSettingsV2;
